import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ArrowRight, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';

export default function Cart() {
  const { cart, updateQuantity, removeFromCart, getTotalPrice } = useCart();
  const navigate = useNavigate();

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-24 flex items-center justify-center">
        <div className="text-center" data-testid="empty-cart">
          <ShoppingBag className="w-24 h-24 text-gray-300 mx-auto mb-6" />
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Корзина пуста
          </h2>
          <p className="text-gray-600 mb-8">Добавьте товары в корзину, чтобы оформить заказ</p>
          <Link to="/catalog">
            <Button className="bg-sky-600 hover:bg-sky-700 text-white">
              Перейти в каталог
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-4xl font-bold mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }} data-testid="cart-title">
          Корзина
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
            {cart.map(item => (
              <div key={item.product.id} className="bg-white rounded-xl p-6 shadow-md flex items-center space-x-6" data-testid={`cart-item-${item.product.id}`}>
                <img
                  src={item.product.image_url || 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38'}
                  alt={item.product.name}
                  className="w-24 h-24 object-cover rounded-lg"
                />
                
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-2">{item.product.name}</h3>
                  <p className="text-sm text-gray-600 mb-2">{item.product.origin}</p>
                  <p className="text-xl font-bold text-sky-600">
                    {item.product.price.toLocaleString('ru-RU')} ₽
                  </p>
                </div>

                <div className="flex items-center space-x-3">
                  <Button
                    onClick={() => updateQuantity(item.product.id, item.quantity - 1)}
                    data-testid="decrease-cart-quantity"
                    variant="outline"
                    size="icon"
                    className="w-8 h-8"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="text-lg font-bold w-8 text-center" data-testid="cart-item-quantity">{item.quantity}</span>
                  <Button
                    onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                    data-testid="increase-cart-quantity"
                    variant="outline"
                    size="icon"
                    className="w-8 h-8"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                <div className="text-right">
                  <p className="text-xl font-bold mb-2">
                    {(item.product.price * item.quantity).toLocaleString('ru-RU')} ₽
                  </p>
                  <Button
                    onClick={() => removeFromCart(item.product.id)}
                    data-testid="remove-from-cart"
                    variant="ghost"
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 shadow-md sticky top-24" data-testid="order-summary">
              <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Итого
              </h2>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-gray-600">
                  <span>Товаров:</span>
                  <span>{cart.length}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Сумма:</span>
                  <span>{getTotalPrice().toLocaleString('ru-RU')} ₽</span>
                </div>
                <div className="pt-4 border-t">
                  <div className="flex justify-between text-2xl font-bold">
                    <span>Итого:</span>
                    <span className="text-sky-600" data-testid="cart-total">{getTotalPrice().toLocaleString('ru-RU')} ₽</span>
                  </div>
                </div>
              </div>

              <Button
                onClick={() => navigate('/checkout')}
                data-testid="proceed-to-checkout"
                className="w-full bg-sky-600 hover:bg-sky-700 text-white h-12"
              >
                Оформить заказ
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>

              <Link to="/catalog" className="block text-center mt-4">
                <Button variant="ghost" className="w-full">
                  Продолжить покупки
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}